import React, { useState, useRef } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { mergeBrandingWithDefaults } from '../../types/tournamentTypes';
import ImageCropper from './ImageCropper';
import { generateTournamentQRCode, downloadQRCode, getTournamentShareUrl } from '../../utils/qrCodeUtils';

export default function BrandingModal({ tournament, onClose, onSave }) {
  const existingBranding = mergeBrandingWithDefaults(tournament.branding);

  const [branding, setBranding] = useState(existingBranding);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [saving, setSaving] = useState(false);
  const [cropperState, setCropperState] = useState(null); // { file, type, aspectRatio, maxWidth, maxHeight }

  const logoInputRef = useRef(null);
  const bannerInputRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes (increased for initial upload before cropping)

  const handleColorChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTextChange = (field, value) => {
    setBranding(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSocialLinkChange = (platform, value) => {
    setBranding(prev => ({
      ...prev,
      socialLinks: {
        ...prev.socialLinks,
        [platform]: value
      }
    }));
  };

  const handleToggleChange = (field) => {
    setBranding(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateFile = (file) => {
    if (!file) return { valid: false, error: 'No file selected' };

    // Check file type
    if (!file.type.startsWith('image/')) {
      return { valid: false, error: 'File must be an image' };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File size must be less than 2MB (current: ${(file.size / 1024 / 1024).toFixed(2)}MB)` };
    }

    return { valid: true };
  };

  const uploadImage = async (file, type) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return null;
    }

    setUploading(true);
    setUploadProgress(`Uploading ${type}...`);

    try {
      // Create a unique filename
      const timestamp = Date.now();
      const filename = `tournaments/${tournament.id}/branding/${type}_${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadURL = await getDownloadURL(storageRef);

      setUploadProgress('');
      setUploading(false);

      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      alert(`Failed to upload ${type}: ${error.message}`);
      setUploadProgress('');
      setUploading(false);
      return null;
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      if (logoInputRef.current) {
        logoInputRef.current.value = '';
      }
      return;
    }

    // Open cropper for logo (square aspect ratio, max 400x400)
    setCropperState({
      file,
      type: 'logo',
      aspectRatio: 1, // Square
      maxWidth: 400,
      maxHeight: 400
    });

    // Reset input
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
      return;
    }

    // Open cropper for banner (wide aspect ratio 3:1, max 1200x400)
    setCropperState({
      file,
      type: 'banner',
      aspectRatio: 3, // 3:1 wide
      maxWidth: 1200,
      maxHeight: 400
    });

    // Reset input
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }
  };

  const handleCropComplete = async (croppedFile) => {
    const { type } = cropperState;
    setCropperState(null);

    const downloadURL = await uploadImage(croppedFile, type);
    if (downloadURL) {
      if (type === 'logo') {
        setBranding(prev => ({
          ...prev,
          logoUrl: downloadURL
        }));
      } else if (type === 'banner') {
        setBranding(prev => ({
          ...prev,
          bannerImageUrl: downloadURL
        }));
      }
    }
  };

  const handleCropCancel = () => {
    setCropperState(null);
  };

  const handleRemoveLogo = () => {
    setBranding(prev => ({
      ...prev,
      logoUrl: ''
    }));
  };

  const handleRemoveBanner = () => {
    setBranding(prev => ({
      ...prev,
      bannerImageUrl: ''
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(branding);
      onClose();
    } catch (error) {
      console.error('Error saving branding:', error);
      alert('Failed to save branding changes. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Image Cropper Modal */}
      {cropperState && (
        <ImageCropper
          imageFile={cropperState.file}
          aspectRatio={cropperState.aspectRatio}
          maxWidth={cropperState.maxWidth}
          maxHeight={cropperState.maxHeight}
          onComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}

      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'var(--bg-modal-overlay)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px'
      }}>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '8px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'var(--shadow-lg)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '2px solid var(--border-color)'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 'bold',
            color: 'var(--text-primary)'
          }}>
            Edit Tournament Branding
          </h2>
        </div>

        {/* Scrollable Content */}
        <div style={{
          padding: '24px',
          overflowY: 'auto',
          flex: 1
        }}>
          {/* Promotional Announcement Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>📢</span> Promotional Announcement
            </h3>

            <div style={{
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              marginBottom: '12px',
              backgroundColor: 'var(--bg-tertiary)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={branding.announcement?.enabled || false}
                  onChange={() => setBranding(prev => ({
                    ...prev,
                    announcement: {
                      ...prev.announcement,
                      enabled: !prev.announcement?.enabled
                    }
                  }))}
                  style={{
                    marginRight: '12px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-primary)'
                  }}>
                    Show Promotional Banner
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginTop: '4px'
                  }}>
                    Display a highlighted announcement at the top of your tournament
                  </div>
                </div>
              </label>
            </div>

            {branding.announcement?.enabled && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)',
                    marginBottom: '8px'
                  }}>
                    Announcement Message
                  </label>
                  <input
                    type="text"
                    value={branding.announcement?.message || ''}
                    onChange={(e) => setBranding(prev => ({
                      ...prev,
                      announcement: { ...prev.announcement, message: e.target.value }
                    }))}
                    maxLength={100}
                    placeholder="e.g., Happy Hour 4-7pm! $2 off all drafts during tournament"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    margin: '4px 0 0 0'
                  }}>
                    Short promotional message ({(branding.announcement?.message || '').length}/100)
                  </p>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-secondary)',
                      marginBottom: '8px'
                    }}>
                      Button Text (optional)
                    </label>
                    <input
                      type="text"
                      value={branding.announcement?.ctaText || ''}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        announcement: { ...prev.announcement, ctaText: e.target.value }
                      }))}
                      maxLength={20}
                      placeholder="e.g., View Menu"
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-secondary)',
                      marginBottom: '8px'
                    }}>
                      Button Link (optional)
                    </label>
                    <input
                      type="text"
                      value={branding.announcement?.ctaUrl || ''}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        announcement: { ...prev.announcement, ctaUrl: e.target.value }
                      }))}
                      placeholder="https://..."
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        fontSize: '14px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Colors Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Colors
            </h3>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Primary Color
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: branding.primaryColor,
                        borderRadius: '4px',
                        border: '2px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('primaryColorPicker').click()}
                    />
                    <input
                      id="primaryColorPicker"
                      type="color"
                      value={branding.primaryColor}
                      onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '36px',
                        height: '36px',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <input
                    type="text"
                    value={branding.primaryColor}
                    onChange={(e) => handleColorChange('primaryColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}
                    placeholder="#007bff"
                  />
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Secondary Color
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: branding.secondaryColor,
                        borderRadius: '4px',
                        border: '2px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('secondaryColorPicker').click()}
                    />
                    <input
                      id="secondaryColorPicker"
                      type="color"
                      value={branding.secondaryColor}
                      onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '36px',
                        height: '36px',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <input
                    type="text"
                    value={branding.secondaryColor}
                    onChange={(e) => handleColorChange('secondaryColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}
                    placeholder="#6c757d"
                  />
                </div>
              </div>

              <div style={{ flex: '1', minWidth: '150px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Accent Color
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        width: '36px',
                        height: '36px',
                        backgroundColor: branding.accentColor || '#28a745',
                        borderRadius: '4px',
                        border: '2px solid var(--border-color)',
                        cursor: 'pointer'
                      }}
                      onClick={() => document.getElementById('accentColorPicker').click()}
                    />
                    <input
                      id="accentColorPicker"
                      type="color"
                      value={branding.accentColor || '#28a745'}
                      onChange={(e) => handleColorChange('accentColor', e.target.value)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '36px',
                        height: '36px',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                  </div>
                  <input
                    type="text"
                    value={branding.accentColor || '#28a745'}
                    onChange={(e) => handleColorChange('accentColor', e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px',
                      fontSize: '12px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontFamily: 'monospace'
                    }}
                    placeholder="#28a745"
                  />
                </div>
              </div>
            </div>
            <p style={{
              fontSize: '12px',
              color: 'var(--text-secondary)',
              margin: '8px 0 0 0'
            }}>
              Primary: titles & buttons | Secondary: backgrounds | Accent: highlights & CTAs
            </p>
          </div>

          {/* Theme Style Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Header Theme
            </h3>

            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {[
                { value: 'default', label: 'Light', preview: '#fff' },
                { value: 'dark', label: 'Dark', preview: '#1a1a2e' },
                { value: 'gradient', label: 'Gradient', preview: 'linear-gradient(135deg, #667eea, #764ba2)' }
              ].map(theme => (
                <button
                  key={theme.value}
                  onClick={() => setBranding(prev => ({
                    ...prev,
                    theme: { ...prev.theme, style: theme.value }
                  }))}
                  style={{
                    padding: '12px 20px',
                    border: branding.theme?.style === theme.value ? '3px solid var(--btn-primary)' : '2px solid var(--border-color)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    backgroundColor: 'var(--bg-secondary)',
                    transition: 'all 0.2s'
                  }}
                >
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '4px',
                    background: theme.preview,
                    border: '1px solid var(--border-color)'
                  }} />
                  <span style={{ fontWeight: branding.theme?.style === theme.value ? '600' : '400' }}>
                    {theme.label}
                  </span>
                </button>
              ))}
            </div>

            {branding.theme?.style === 'gradient' && (
              <div style={{ display: 'flex', gap: '16px', marginTop: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)',
                    marginBottom: '8px'
                  }}>
                    Gradient Start
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={branding.theme?.gradientStart || '#667eea'}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        theme: { ...prev.theme, gradientStart: e.target.value }
                      }))}
                      style={{
                        width: '50px',
                        height: '36px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={branding.theme?.gradientStart || '#667eea'}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        theme: { ...prev.theme, gradientStart: e.target.value }
                      }))}
                      style={{
                        flex: 1,
                        padding: '8px',
                        fontSize: '12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-secondary)',
                    marginBottom: '8px'
                  }}>
                    Gradient End
                  </label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={branding.theme?.gradientEnd || '#764ba2'}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        theme: { ...prev.theme, gradientEnd: e.target.value }
                      }))}
                      style={{
                        width: '50px',
                        height: '36px',
                        border: '2px solid var(--border-color)',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                    />
                    <input
                      type="text"
                      value={branding.theme?.gradientEnd || '#764ba2'}
                      onChange={(e) => setBranding(prev => ({
                        ...prev,
                        theme: { ...prev.theme, gradientEnd: e.target.value }
                      }))}
                      style={{
                        flex: 1,
                        padding: '8px',
                        fontSize: '12px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        fontFamily: 'monospace'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Images Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Images
            </h3>

            {/* Logo Upload */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Tournament Logo
              </label>

              {branding.logoUrl ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-tertiary)'
                }}>
                  <img
                    src={branding.logoUrl}
                    alt="Tournament Logo"
                    style={{
                      width: '60px',
                      height: '60px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '2px solid var(--border-color)'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: 'var(--success)',
                      fontWeight: '600'
                    }}>
                      Logo uploaded
                    </p>
                  </div>
                  <button
                    onClick={handleRemoveLogo}
                    disabled={uploading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--danger)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1
                    }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'var(--btn-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1,
                      width: '100%'
                    }}
                  >
                    Upload Logo
                  </button>
                </div>
              )}

              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: '4px 0 0 0'
              }}>
                Will be cropped to square and resized to 400x400px. Max 10MB.
              </p>
            </div>

            {/* Banner Upload */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Banner Image
              </label>

              {branding.bannerImageUrl ? (
                <div style={{
                  padding: '12px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  backgroundColor: 'var(--bg-tertiary)'
                }}>
                  <img
                    src={branding.bannerImageUrl}
                    alt="Banner"
                    style={{
                      width: '100%',
                      height: '120px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '2px solid var(--border-color)',
                      marginBottom: '12px'
                    }}
                  />
                  <button
                    onClick={handleRemoveBanner}
                    disabled={uploading}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'var(--danger)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1,
                      width: '100%'
                    }}
                  >
                    Remove Banner
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={bannerInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleBannerUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => bannerInputRef.current?.click()}
                    disabled={uploading}
                    style={{
                      padding: '10px 16px',
                      backgroundColor: 'var(--btn-primary)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      opacity: uploading ? 0.6 : 1,
                      width: '100%'
                    }}
                  >
                    Upload Banner
                  </button>
                </div>
              )}

              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: '4px 0 0 0'
              }}>
                Will be cropped to 3:1 ratio and resized to max 1200x400px. Max 10MB.
              </p>
            </div>

            {/* Upload Progress */}
            {uploadProgress && (
              <div style={{
                padding: '12px',
                backgroundColor: '#e7f3ff',
                border: '1px solid #b3d9ff',
                borderRadius: '4px',
                fontSize: '14px',
                color: '#004085',
                textAlign: 'center'
              }}>
                {uploadProgress}
              </div>
            )}
          </div>

          {/* Text Content Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Text Content
            </h3>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Tagline
              </label>
              <input
                type="text"
                value={branding.tagline}
                onChange={(e) => handleTextChange('tagline', e.target.value)}
                maxLength={100}
                placeholder="e.g., Weekly Pool Tournament"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: '4px 0 0 0'
              }}>
                Short subtitle displayed under tournament title ({branding.tagline.length}/100)
              </p>
            </div>

          </div>

          {/* Social Links Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Social Links
            </h3>

            {['facebook', 'instagram', 'twitter', 'website'].map((platform) => (
              <div key={platform} style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px',
                  textTransform: 'capitalize'
                }}>
                  {platform}
                </label>
                <input
                  type="text"
                  value={branding.socialLinks[platform]}
                  onChange={(e) => handleSocialLinkChange(platform, e.target.value)}
                  placeholder={`https://${platform === 'website' ? 'example.com' : platform + '.com'}/...`}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            ))}
          </div>

          {/* Venue Info Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>🏢</span> Venue Information
            </h3>
            <p style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              margin: '0 0 16px 0'
            }}>
              Additional venue details to help players (separate from tournament location)
            </p>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Venue Hours
                </label>
                <input
                  type="text"
                  value={branding.venue?.hours || ''}
                  onChange={(e) => setBranding(prev => ({
                    ...prev,
                    venue: { ...prev.venue, hours: e.target.value }
                  }))}
                  placeholder="e.g., Mon-Sat 4pm-2am, Sun 12pm-12am"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  value={branding.venue?.phone || ''}
                  onChange={(e) => setBranding(prev => ({
                    ...prev,
                    venue: { ...prev.venue, phone: e.target.value }
                  }))}
                  placeholder="e.g., (555) 123-4567"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Menu / Specials Link
              </label>
              <input
                type="text"
                value={branding.venue?.menuUrl || ''}
                onChange={(e) => setBranding(prev => ({
                  ...prev,
                  venue: { ...prev.venue, menuUrl: e.target.value }
                }))}
                placeholder="https://example.com/menu"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: '4px 0 0 0'
              }}>
                Link to your food/drink menu or current specials
              </p>
            </div>
          </div>

          {/* Prize Display Section */}
          <div style={{ marginBottom: '32px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '20px' }}>🏆</span> Prize Display
            </h3>

            <div style={{
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              marginBottom: '16px',
              backgroundColor: 'var(--bg-tertiary)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={branding.prizeDisplay?.showBreakdown !== false}
                  onChange={() => setBranding(prev => ({
                    ...prev,
                    prizeDisplay: {
                      ...prev.prizeDisplay,
                      showBreakdown: !prev.prizeDisplay?.showBreakdown
                    }
                  }))}
                  style={{
                    marginRight: '12px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-primary)'
                  }}>
                    Show Prize Breakdown
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginTop: '4px'
                  }}>
                    Display calculated payouts based on entry fees and payout structure
                  </div>
                </div>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Added Money
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '16px', color: 'var(--text-secondary)' }}>$</span>
                  <input
                    type="number"
                    min="0"
                    value={branding.prizeDisplay?.addedMoney || ''}
                    onChange={(e) => setBranding(prev => ({
                      ...prev,
                      prizeDisplay: {
                        ...prev.prizeDisplay,
                        addedMoney: parseInt(e.target.value) || 0
                      }
                    }))}
                    placeholder="0"
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      fontSize: '14px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '600',
                  color: 'var(--text-secondary)',
                  marginBottom: '8px'
                }}>
                  Added By
                </label>
                <input
                  type="text"
                  value={branding.prizeDisplay?.addedMoneySource || ''}
                  onChange={(e) => setBranding(prev => ({
                    ...prev,
                    prizeDisplay: {
                      ...prev.prizeDisplay,
                      addedMoneySource: e.target.value
                    }
                  }))}
                  placeholder="e.g., House Added, Sponsor Name"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    fontSize: '14px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Custom Prizes */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-secondary)',
                marginBottom: '8px'
              }}>
                Custom Prize Descriptions (optional)
              </label>
              <p style={{
                fontSize: '12px',
                color: 'var(--text-secondary)',
                margin: '0 0 12px 0'
              }}>
                Override default prize display with custom descriptions (e.g., trophies, gift cards)
              </p>

              {(branding.prizeDisplay?.customPrizes || []).map((prize, index) => (
                <div key={index} style={{
                  display: 'flex',
                  gap: '8px',
                  marginBottom: '8px',
                  alignItems: 'center'
                }}>
                  <select
                    value={prize.place}
                    onChange={(e) => {
                      const newPrizes = [...(branding.prizeDisplay?.customPrizes || [])];
                      newPrizes[index] = { ...prize, place: parseInt(e.target.value) };
                      setBranding(prev => ({
                        ...prev,
                        prizeDisplay: { ...prev.prizeDisplay, customPrizes: newPrizes }
                      }));
                    }}
                    style={{
                      padding: '8px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  >
                    <option value={1}>1st Place</option>
                    <option value={2}>2nd Place</option>
                    <option value={3}>3rd Place</option>
                    <option value={4}>4th Place</option>
                  </select>
                  <input
                    type="text"
                    value={prize.description}
                    onChange={(e) => {
                      const newPrizes = [...(branding.prizeDisplay?.customPrizes || [])];
                      newPrizes[index] = { ...prize, description: e.target.value };
                      setBranding(prev => ({
                        ...prev,
                        prizeDisplay: { ...prev.prizeDisplay, customPrizes: newPrizes }
                      }));
                    }}
                    placeholder="e.g., Trophy + $100 + Free Entry"
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                  />
                  <button
                    onClick={() => {
                      const newPrizes = (branding.prizeDisplay?.customPrizes || []).filter((_, i) => i !== index);
                      setBranding(prev => ({
                        ...prev,
                        prizeDisplay: { ...prev.prizeDisplay, customPrizes: newPrizes }
                      }));
                    }}
                    style={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--danger)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}

              <button
                onClick={() => {
                  const existingPlaces = (branding.prizeDisplay?.customPrizes || []).map(p => p.place);
                  const nextPlace = [1, 2, 3, 4].find(p => !existingPlaces.includes(p)) || 1;
                  setBranding(prev => ({
                    ...prev,
                    prizeDisplay: {
                      ...prev.prizeDisplay,
                      customPrizes: [
                        ...(prev.prizeDisplay?.customPrizes || []),
                        { place: nextPlace, description: '' }
                      ]
                    }
                  }));
                }}
                disabled={(branding.prizeDisplay?.customPrizes || []).length >= 4}
                style={{
                  padding: '8px 16px',
                  backgroundColor: (branding.prizeDisplay?.customPrizes || []).length >= 4 ? '#ccc' : 'var(--success)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: (branding.prizeDisplay?.customPrizes || []).length >= 4 ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  marginTop: '8px'
                }}
              >
                + Add Custom Prize
              </button>
            </div>
          </div>

          {/* Display Settings Section */}
          <div style={{ marginBottom: '16px' }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: 'var(--text-primary)',
              marginBottom: '16px',
              marginTop: 0
            }}>
              Display Settings
            </h3>

            <div style={{
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              marginBottom: '12px',
              backgroundColor: 'var(--bg-tertiary)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={branding.showSplashScreen}
                  onChange={() => handleToggleChange('showSplashScreen')}
                  style={{
                    marginRight: '12px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-primary)'
                  }}>
                    Show Splash Screen
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginTop: '4px'
                  }}>
                    Display branded header at top of tournament page
                  </div>
                </div>
              </label>
            </div>

            <div style={{
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              backgroundColor: 'var(--bg-tertiary)'
            }}>
              <label style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  checked={branding.autoCollapse}
                  onChange={() => handleToggleChange('autoCollapse')}
                  style={{
                    marginRight: '12px',
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer'
                  }}
                />
                <div>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-primary)'
                  }}>
                    Auto-collapse Header
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginTop: '4px'
                  }}>
                    Automatically collapse header when scrolling down
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* QR Code Section */}
          {tournament.shareUrl && (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--text-primary)',
                marginBottom: '16px',
                marginTop: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '20px' }}>📱</span> QR Code for Sharing
              </h3>
              <p style={{
                fontSize: '13px',
                color: 'var(--text-secondary)',
                margin: '0 0 16px 0'
              }}>
                Download and print this QR code for flyers, posters, and bar signage. Players can scan to view tournament details.
              </p>

              <div style={{
                display: 'flex',
                gap: '20px',
                alignItems: 'center',
                padding: '16px',
                backgroundColor: 'var(--bg-tertiary)',
                borderRadius: '8px',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{
                  backgroundColor: 'var(--bg-secondary)',
                  padding: '8px',
                  borderRadius: '8px',
                  boxShadow: 'var(--shadow-sm)'
                }}>
                  <img
                    src={generateTournamentQRCode(tournament.shareUrl, 150)}
                    alt="Tournament QR Code"
                    style={{
                      width: '150px',
                      height: '150px',
                      display: 'block'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: 'var(--text-primary)',
                    marginBottom: '8px'
                  }}>
                    Tournament URL
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '16px',
                    wordBreak: 'break-all',
                    padding: '8px',
                    backgroundColor: 'var(--bg-secondary)',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)'
                  }}>
                    {getTournamentShareUrl(tournament.shareUrl)}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => {
                        const qrUrl = generateTournamentQRCode(tournament.shareUrl, 400);
                        const filename = `${tournament.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-qr-code.png`;
                        downloadQRCode(qrUrl, filename);
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--success)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>⬇️</span> Download QR Code
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(getTournamentShareUrl(tournament.shareUrl));
                        alert('URL copied to clipboard!');
                      }}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: 'var(--btn-primary)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <span>📋</span> Copy URL
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px'
        }}>
          <button
            onClick={onClose}
            disabled={uploading || saving}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--btn-secondary)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (uploading || saving) ? 'not-allowed' : 'pointer',
              opacity: (uploading || saving) ? 0.6 : 1
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={uploading || saving}
            style={{
              padding: '10px 20px',
              backgroundColor: 'var(--success)',
              color: '#fff',
              border: 'none',
              borderRadius: '4px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: (uploading || saving) ? 'not-allowed' : 'pointer',
              opacity: (uploading || saving) ? 0.6 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}
